import React from 'react';
import { Code, Palette, CheckCircle, ArrowRight } from 'lucide-react';

const Programs = () => {
  return (
    <section id="apply" className="py-24 bg-white border-t border-slate-100">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">Available Programmes</h2>
          <p className="text-lg text-slate-600 max-w-3xl">
            Select a track to begin your structured contribution journey.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Full Stack Web Development */}
          <div className="border border-slate-200 rounded-sm p-8 flex flex-col hover:border-slate-400 transition-colors">
            <div className="mb-6">
              <div className="inline-flex items-center space-x-2 bg-slate-50 px-3 py-1 rounded-full mb-4">
                <Code className="w-4 h-4 text-emerald-600" />
                <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Technical Track</span>
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-2">Full Stack Web Development</h3>
              <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">3 Months | Structured Contribution Track</p>
            </div>

            <p className="text-slate-600 mb-6 leading-relaxed">
              Develop production-grade web systems from frontend architecture to deployment under guided sprint cycles.
              <br/><br/>
              This internship is contribution-based and performance-evaluated.
            </p>

            <div className="mb-6">
              <p className="font-semibold text-slate-900 mb-3">Participants work within:</p>
              <ul className="space-y-2">
                {[
                  "React-driven UI architecture",
                  "Backend API design with Node.js",
                  "Database structuring with MongoDB",
                  "Deployment workflows and version control",
                  "Code review and architectural supervision"
                ].map((item, i) => (
                  <li key={i} className="flex items-start text-sm text-slate-600">
                    <span className="w-1.5 h-1.5 bg-slate-400 rounded-full mt-1.5 mr-2 shrink-0"></span>
                    {item}
                  </li>
                ))}
              </ul>
              <p className="text-sm text-slate-500 italic mt-3">All work is delivered through governed contribution cycles.</p>
            </div>

            <div className="mb-8 p-4 bg-slate-50 rounded-sm">
              <p className="text-xs font-bold text-slate-500 uppercase mb-2">Technologies</p>
              <p className="text-sm text-slate-800 font-mono">React · Node.js · MongoDB · REST APIs · Git · Deployment Workflows</p>
            </div>

            <div className="mt-auto pt-8 border-t border-slate-100">
              <p className="font-semibold text-slate-900 mb-4">Program Structure Includes:</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                {[
                  "Structured skill screening",
                  "Tier-based project allocation",
                  "Mentor-reviewed sprint cycles",
                  "Documented performance tracking",
                  "Verified internship credential",
                  "Contribution summary record"
                ].map((item, i) => (
                  <div key={i} className="flex items-center text-sm text-slate-600">
                    <CheckCircle className="w-4 h-4 text-emerald-500 mr-2 shrink-0" />
                    {item}
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-slate-900 p-6 rounded-sm text-white">
                <div>
                  <p className="text-sm text-slate-400 mb-1">Total Fee</p>
                  <p className="text-3xl font-bold">₹2,995</p>
                  <p className="text-xs text-slate-400 mt-1 max-w-[200px] leading-tight">(Covers mentorship, review cycles, governance & certification)</p>
                </div>
                <button className="w-full sm:w-auto px-6 py-3 bg-white text-slate-900 font-semibold rounded-sm hover:bg-slate-100 transition-colors flex items-center justify-center">
                  View Curriculum
                  <ArrowRight className="ml-2 w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Graphic Design */}
          <div className="border border-slate-200 rounded-sm p-8 flex flex-col hover:border-slate-400 transition-colors">
            <div className="mb-6">
              <div className="inline-flex items-center space-x-2 bg-slate-50 px-3 py-1 rounded-full mb-4">
                <Palette className="w-4 h-4 text-purple-600" />
                <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Creative Track</span>
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-2">Graphic Design Internship</h3>
              <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">3 Months | Structured Creative Contribution Track</p>
            </div>

            <p className="text-slate-600 mb-6 leading-relaxed">
              Build professional branding systems, campaign assets, and UI case studies under defined creative direction and review cycles.
              <br/><br/>
              Participants operate within structured design sprints and performance-evaluated submissions.
            </p>

             <div className="mb-6">
              <p className="font-semibold text-slate-900 mb-3">Focus Areas:</p>
              <ul className="space-y-2">
                 <li className="flex items-start text-sm text-slate-600">
                    <span className="w-1.5 h-1.5 bg-slate-400 rounded-full mt-1.5 mr-2 shrink-0"></span>
                    Professional Branding Systems
                  </li>
                  <li className="flex items-start text-sm text-slate-600">
                    <span className="w-1.5 h-1.5 bg-slate-400 rounded-full mt-1.5 mr-2 shrink-0"></span>
                    Campaign Asset Creation
                  </li>
                  <li className="flex items-start text-sm text-slate-600">
                    <span className="w-1.5 h-1.5 bg-slate-400 rounded-full mt-1.5 mr-2 shrink-0"></span>
                    UI Case Studies
                  </li>
              </ul>
              <p className="text-sm text-slate-500 italic mt-3">This internship emphasizes portfolio-grade output.</p>
            </div>

            <div className="mb-8 p-4 bg-slate-50 rounded-sm">
              <p className="text-xs font-bold text-slate-500 uppercase mb-2">Tools & Platforms</p>
              <p className="text-sm text-slate-800 font-mono">Photoshop · Illustrator · Figma · Branding Systems · UI Workflows</p>
            </div>

            <div className="mt-auto pt-8 border-t border-slate-100">
              <p className="font-semibold text-slate-900 mb-4">Program Structure Includes:</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                {[
                  "Portfolio assessment screening",
                  "Project-based creative allocation",
                  "Structured review and critique",
                  "Performance documentation",
                  "Verified internship credential",
                  "Contribution summary record"
                ].map((item, i) => (
                  <div key={i} className="flex items-center text-sm text-slate-600">
                    <CheckCircle className="w-4 h-4 text-emerald-500 mr-2 shrink-0" />
                    {item}
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-slate-900 p-6 rounded-sm text-white">
                <div>
                  <p className="text-sm text-slate-400 mb-1">Total Fee</p>
                  <p className="text-3xl font-bold">₹1,995</p>
                  <p className="text-xs text-slate-400 mt-1 max-w-[200px] leading-tight">(Covers mentorship, review sessions, & certification)</p>
                </div>
                <button className="w-full sm:w-auto px-6 py-3 bg-white text-slate-900 font-semibold rounded-sm hover:bg-slate-100 transition-colors flex items-center justify-center">
                  View Curriculum
                  <ArrowRight className="ml-2 w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Programs;
